/* Share-grid text builder. Output is:

   1. Header — "PALINDRAGON ID"
   2. Variable-height block of emoji rows, each preserving the solution's
      spacing and punctuation:
        - submitted guesses → 🟩/🟨/⬛ feedback
        - hint-paid slots   → 🟥 (when the player bought the hint, those
                               two slots are visually charged to the cost)
      Before the first move, the block is a single empty-solution row of
      ⬜ (the shape of the puzzle, no progress). Once any slot has been
      used (a submission or a hint purchase), the block grows to cover
      every used slot and stops there — no trailing ⬜ rows.
   3. Footer — "X/Y • HINT AT N" (or "X/Y • NO HINT"). X is total slots
      used (submissions + hint cost); Y is maxGuesses; N is 1-based and
      identifies the slot the hint was purchased into. Win/loss is
      conveyed by the emoji rows themselves, not the footer.

   Hint-paid rows sit at the slots where they were purchased (mirroring
   the board layout): submitted rows before the purchase keep their slots,
   later submissions get pushed down by HINT_COST.

   Submitted-empty cells share the gray emoji with not-in-solution cells:
   from a viewer's standpoint both are "no info revealed", and conflating
   them keeps the share grid in Wordle's two-state vocabulary (hit / miss). */

import type { Mark } from "./scoring";

const EMOJI: Record<Mark, string> = {
  green: "🟩",
  yellow: "🟨",
  gray: "⬛",
  empty: "⬛",
};

const PLACEHOLDER = "⬜";
const HINT_FILL = "🟥";

const LETTER_RE = /[a-z]/i;

/** Substitute emojis at letter positions of `sentence`; pass non-letter
    characters (spaces, commas, em-dashes…) through verbatim. */
function renderRow(marks: readonly Mark[], sentence: string): string {
  let out = "";
  let li = 0;
  for (const ch of sentence) {
    if (LETTER_RE.test(ch)) {
      out += EMOJI[marks[li++]!];
    } else {
      out += ch;
    }
  }
  return out;
}

/** Same shape as renderRow, but every letter position is filled with the
    given character (⬜ for unplayed slots, 🟥 for hint-paid slots). */
function renderFilledRow(sentence: string, fill: string): string {
  let out = "";
  for (const ch of sentence) {
    out += LETTER_RE.test(ch) ? fill : ch;
  }
  return out;
}

export function buildShareText(
  marks: readonly (readonly Mark[])[],
  puzzleId: string,
  sentence: string,
  maxGuesses: number,
  hintPurchasedAt: number | null,
  hintCost: number,
): string {
  const header = `PALINDRAGON ${puzzleId}`;

  // Lay out the maxGuesses-sized board, then trim to the used range so
  // the block grows as the player plays. Submitted rows occupy their
  // visual slots (mirrors Board.tsx's slotForGuess logic).
  const placeholderRow = renderFilledRow(sentence, PLACEHOLDER);
  const hintRow = renderFilledRow(sentence, HINT_FILL);
  const rows: string[] = new Array(maxGuesses).fill(placeholderRow);
  const used: boolean[] = new Array(maxGuesses).fill(false);

  if (hintPurchasedAt != null) {
    for (let i = 0; i < hintCost; i++) {
      const slot = hintPurchasedAt + i;
      if (slot < maxGuesses) {
        rows[slot] = hintRow;
        used[slot] = true;
      }
    }
  }

  for (let g = 0; g < marks.length; g++) {
    const slot = hintPurchasedAt != null && g >= hintPurchasedAt
      ? g + hintCost
      : g;
    if (slot < maxGuesses) {
      rows[slot] = renderRow(marks[g]!, sentence);
      used[slot] = true;
    }
  }

  let lastUsed = -1;
  for (let i = used.length - 1; i >= 0; i--) {
    if (used[i]) {
      lastUsed = i;
      break;
    }
  }
  const visibleRows = lastUsed >= 0
    ? rows.slice(0, lastUsed + 1)
    : [placeholderRow];

  const guessesUsed = marks.length + (hintPurchasedAt != null ? hintCost : 0);
  const hintPart = hintPurchasedAt != null
    ? `HINT AT ${hintPurchasedAt + 1}`
    : "NO HINT";
  const footer = `${guessesUsed}/${maxGuesses} • ${hintPart}`;

  return [header, ...visibleRows, footer].join("\n");
}
